import { ThunkDispatch } from '@reduxjs/toolkit';
import { Map } from 'immutable';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchCommunications, fromCommunications } from './communications';

/**
 * A custom hook that retrieves communications from the Redux store for a specific event and optionally filters by phase.
 *
 * @param {string} eventId - The unique identifier of the event to get communications for
 * @param {string} [phaseId] - Optional phase identifier to filter communications by specific phase
 * @returns {Communication[]} The communications data from the Redux store, filtered by event and optionally by phase
 *
 * @example
 * const communications = useCommunications('123');
 * const phaseCommunications = useCommunications('123', '456');
 */
export const useCommunications = (eventId: string, phaseId?: string) => {
  const communications = useSelector((state: Map<string, any>) =>
    fromCommunications.getCommunications(state?.toJS(), eventId, phaseId),
  );

  return communications;
};

/**
 * A custom hook that retrieves a specific communication from the Redux store by its slug for a given event and optionally filters by phase.
 *
 * @param {string} slug - The slug identifier of the communication to retrieve
 * @param {string} eventId - The unique identifier of the event to get the communication for
 * @param {string} [phaseId] - Optional phase identifier to filter the communication by specific phase
 * @returns {Communication|undefined} The communication data from the Redux store matching the slug, or undefined if not found
 *
 * @example
 * const communication = useCommunicationBySlug('welcome-message', '123');
 * const phaseCommunication = useCommunicationBySlug('welcome-message', '123', '456');
 */
export const useCommunicationBySlug = (slug: string, eventId: string, phaseId?: string) => {
  const communication = useSelector((state: Map<string, any>) =>
    fromCommunications.getCommunicationsBySlug(state?.toJS(), slug, eventId, phaseId),
  );

  return communication;
};

/**
 * A custom hook that fetches and manages communications for an event by its ID.
 * The hook automatically fetches the communications on mount.
 *
 * @param {string} eventId - The unique identifier of the event to fetch communications for
 * @returns {Object} An object containing:
 *   - communications: The communications data from the Redux store
 *   - loading: A boolean indicating if communications are currently being fetched
 *
 * @example
 * const { communications, loading } = useFetchCommunications('123');
 * console.log(communications);
 */
export const useFetchCommunications = (eventId: string) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const communications = useCommunications(eventId);

  const loading = useSelector((state: Map<string, any>) =>
    fromCommunications.getIsLoading(state?.toJS()),
  );

  useEffect(() => {
    dispatch(fetchCommunications({ eventId }));
  }, [dispatch, eventId]);

  return { communications, loading };
};
